// Opus configuration by SDP munging — docs/01-architecture.md §4.2, §5.
//
// Everything WebRTC exposes for Opus (in-band FEC, DTX, bitrate ceiling,
// playback bandwidth, frame size) lives in the SDP, not behind a JS API. The
// direction is the part that is easy to get backwards: `a=fmtp` and `a=ptime`
// in a LOCAL description describe what WE want to RECEIVE, so they configure
// the PEER's encoder. Both ends run this code on their own offers and
// answers, so both directions end up configured.
//
// Signature safety: `a=fingerprint` is never read, moved or rewritten here,
// and signSdpFingerprint() signs only that line — munging is therefore
// signature-neutral. The munge must still happen BEFORE setLocalDescription,
// so the description we apply, sign and send is one and the same string.

import type { RungConfig } from '../quality/ladder.js';

/** Opus clamped to a narrowband path (maxplaybackrate) — rung 2 and up. */
export const NARROWBAND_HZ = 8_000;

/**
 * Per-packet overhead on the wire, bytes: IPv4 20 + UDP 8 + RTP 12 +
 * one-byte header extensions ~12 + SRTP auth tag 10. Every packet pays it
 * whether it carries 20 ms or 60 ms of audio, which is why frame size — not
 * bitrate — is the knob that decides whether a call fits on EDGE.
 */
export const PACKET_OVERHEAD_BYTES = 62;

export interface OpusConfig {
  /** `maxaveragebitrate`: the Opus payload ceiling in bits/s. */
  maxAverageBitrateBps: number;
  /** `a=ptime`/`a=maxptime`: the peer's encoder frame size, ms. */
  ptimeMs: number;
  /** `useinbandfec` — on at every rung (docs/01 §4.1: it costs no steady-state delay). */
  inbandFec: boolean;
  /** `usedtx` — near-zero bitrate during silence. */
  dtx: boolean;
  /** `maxplaybackrate`, or null to leave Opus fullband. */
  maxPlaybackRateHz: number | null;
  /** `cbr` — a flat rate the radio's uplink queue can absorb without overshoot. */
  cbr: boolean;
  /** Prefer audio RED (RFC 2198) when the peer offered it. */
  red: boolean;
}

/**
 * The degradation rung's media settings, expressed as Opus SDP parameters —
 * this is where `maxFec`, `narrowband` and `redEnabled` stop being labels:
 *
 * - `maxFec` ⇒ constant bitrate. Opus only emits in-band FEC (LBRR) in SILK
 *   mode, i.e. at the low bitrates and narrow bandwidths the rung already
 *   forces; pinning the rate flat on top keeps the FEC copies inside the
 *   ceiling instead of turning them into queueing delay.
 * - `narrowband` ⇒ maxplaybackrate=8000, which also keeps the encoder in SILK.
 * - `redEnabled` ⇒ RED preferred ahead of bare Opus on the m-line.
 */
export function opusConfigForRung(rung: RungConfig): OpusConfig {
  return {
    maxAverageBitrateBps: rung.maxBitrateBps,
    ptimeMs: rung.ptimeMs,
    inbandFec: true,
    dtx: true,
    maxPlaybackRateHz: rung.narrowband ? NARROWBAND_HZ : null,
    cbr: rung.maxFec,
    red: rung.redEnabled,
  };
}

/**
 * Rewrite the Opus parameters of a local description. SDP with no Opus m-line
 * (or none we recognise) is returned byte-identical, and applying the same
 * config twice is a no-op — every attribute we own is replaced, never appended.
 */
export function applyOpusConfig(sdp: string, config: OpusConfig): string {
  const lines = sdp.split(/\r\n|\n/);
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n';
  let changed = false;

  for (let i = 0; i < lines.length; i++) {
    if (!lines[i]!.startsWith('m=audio ')) continue;
    let end = i + 1;
    while (end < lines.length && !lines[end]!.startsWith('m=')) end++;
    const shaped = shapeAudioSection(lines.slice(i, end), config);
    if (shaped) {
      lines.splice(i, end - i, ...shaped);
      changed = true;
      i += shaped.length - 1;
    } else {
      i = end - 1;
    }
  }
  return changed ? lines.join(eol) : sdp;
}

/** The shaped lines of one audio m-section, or null when it carries no Opus. */
function shapeAudioSection(section: readonly string[], config: OpusConfig): string[] | null {
  const opusPt = payloadTypeFor(section, 'opus');
  if (opusPt === null) return null;
  const redPt = payloadTypeFor(section, 'red');
  const preferred = config.red && redPt !== null ? redPt : opusPt;
  const fmtpPrefix = `a=fmtp:${opusPt} `;
  const rtpmapPrefix = `a=rtpmap:${opusPt} `;
  const hasFmtp = section.some((l) => l.startsWith(fmtpPrefix));
  const params = fmtpParams(config);

  const out: string[] = [];
  for (const line of section) {
    if (line.startsWith('m=audio ')) {
      out.push(preferPayloadType(line, preferred));
    } else if (/^a=(ptime|maxptime):/.test(line)) {
      // Dropped here, re-emitted as a pair below: rewriting in place is what
      // makes a second munge produce the identical string.
      continue;
    } else if (line.startsWith(fmtpPrefix)) {
      out.push(fmtpPrefix + mergeFmtpParams(line.slice(fmtpPrefix.length), params));
      out.push(`a=ptime:${config.ptimeMs}`, `a=maxptime:${config.ptimeMs}`);
    } else {
      out.push(line);
      if (!hasFmtp && line.startsWith(rtpmapPrefix)) {
        out.push(fmtpPrefix + mergeFmtpParams('', params));
        out.push(`a=ptime:${config.ptimeMs}`, `a=maxptime:${config.ptimeMs}`);
      }
    }
  }
  return out;
}

/** `a=rtpmap:<pt> <codec>/…` lookup inside one m-section. */
function payloadTypeFor(section: readonly string[], codec: string): string | null {
  const re = new RegExp(`^a=rtpmap:(\\d+)\\s+${codec}/`, 'i');
  for (const line of section) {
    const m = re.exec(line);
    if (m) return m[1]!;
  }
  return null;
}

/** Move one payload type to the front of the m-line's preference list. */
function preferPayloadType(mLine: string, pt: string): string {
  const parts = mLine.split(' ');
  const pts = parts.slice(3).filter((p) => p !== '');
  if (!pts.includes(pt)) return mLine;
  return [...parts.slice(0, 3), pt, ...pts.filter((p) => p !== pt)].join(' ');
}

/** Parameters we own, in a fixed order; a null value means "remove it". */
function fmtpParams(config: OpusConfig): ReadonlyMap<string, string | null> {
  return new Map<string, string | null>([
    // The floor stays at Opus's smallest practical frame: ptime, not minptime,
    // is what asks the peer for long frames, and a low floor keeps a peer that
    // ignores ptime from being pushed into a size it cannot encode.
    ['minptime', '10'],
    ['useinbandfec', config.inbandFec ? '1' : '0'],
    ['usedtx', config.dtx ? '1' : '0'],
    ['maxaveragebitrate', String(config.maxAverageBitrateBps)],
    ['maxplaybackrate', config.maxPlaybackRateHz === null ? null : String(config.maxPlaybackRateHz)],
    ['cbr', config.cbr ? '1' : '0'],
  ]);
}

/** Override our keys in an existing fmtp value, preserving the rest. */
function mergeFmtpParams(existing: string, params: ReadonlyMap<string, string | null>): string {
  const kept: string[] = [];
  for (const part of existing.split(';')) {
    const token = part.trim();
    if (token === '') continue;
    const eq = token.indexOf('=');
    const key = (eq < 0 ? token : token.slice(0, eq)).toLowerCase();
    if (params.has(key)) continue; // ours wins — including "ours is: absent"
    kept.push(token);
  }
  for (const [key, value] of params) {
    if (value !== null) kept.push(`${key}=${value}`);
  }
  return kept.join(';');
}
